import type { ReactNode } from "react";

import { Button } from "./Button";
import { I } from "./icons";

export type NoticeTone = "info" | "caution" | "cycle" | "danger";

const DEFAULT_ICON: Record<NoticeTone, ReactNode> = {
  info: I.info,
  caution: I.info,
  cycle: I.drop,
  danger: I.info,
};

interface NoticeAction {
  label: string;
  onClick: () => void;
}

/**
 * A condition with an action (SNIPPETS §11): a tint, a leading icon in the tone's ink, one
 * sentence, at most two buttons. Replaces every old callout, nudge and rescue card.
 * Never a coloured side stripe.
 *
 * When to use: something about today the user can act on or dismiss — a weigh-in overdue, a
 * cycle phase, a failed sync, an install prompt. Not for permanent content (that is a row).
 */
export function Notice({
  tone = "info",
  icon,
  children,
  primary,
  secondary,
  onDismiss,
  title,
  control,
}: {
  tone?: NoticeTone;
  /** a 20px icon; each tone has a default */
  icon?: ReactNode;
  /** one sentence */
  children: ReactNode;
  primary?: NoticeAction;
  secondary?: NoticeAction;
  /** adds a 44px close button at the top right */
  onDismiss?: () => void;
  /** a 15/600 line above the sentence, saying what the control does ("Hot day today — adds 500 ml") */
  title?: ReactNode;
  /** a switch in place of buttons, at the right edge (kit §37's hot-day Notice) */
  control?: ReactNode;
}) {
  return (
    <div
      className={`k-notice${tone !== "info" ? ` k-notice--${tone}` : ""}${control ? " k-notice--control" : ""}`}
      role="note"
    >
      <span className="k-notice__ic" aria-hidden="true">
        {icon ?? DEFAULT_ICON[tone]}
      </span>
      <div className="k-notice__body">
        {title ? <p className="k-notice__title">{title}</p> : null}
        <p className="k-notice__text">{children}</p>
        {primary || secondary ? (
          <div className="k-notice__acts">
            {primary ? <Button onClick={primary.onClick}>{primary.label}</Button> : null}
            {secondary ? (
              <Button variant="ghost" onClick={secondary.onClick}>
                {secondary.label}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      {control}
      {onDismiss ? (
        <button type="button" className="k-iconbtn k-notice__x" onClick={onDismiss} aria-label="Dismiss">
          {I.close}
        </button>
      ) : null}
    </div>
  );
}
